import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Play, ExternalLink, FileText, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Video/Media Card Component
 * Displays a video, PDF, or image card with thumbnail
 */
function UserVideoCard({ videoData, onVideoClick }) {
    const [videoId, setVideoId] = useState('');
    const [isPdfAvailable, setIsPdfAvailable] = useState(false);
    const [isCheckingPdf, setIsCheckingPdf] = useState(false);

    useEffect(() => {
        if (videoData?.mediaLink) {
            const id = videoData.mediaLink.split('v=')[1];
            setVideoId(id || '');
        }

        // Check PDF availability � matching dev-v3 behaviour (res.ok is enough;
        // the server serves real PDF files before the SPA fallback)
        if (videoData?.mediaType === 'application/pdf' && videoData.mediaBase64) {
            setIsCheckingPdf(true);
            fetch(videoData.mediaBase64, { method: 'HEAD' })
                .then((res) => {
                    setIsPdfAvailable(res.ok);
                })
                .catch(() => {
                    setIsPdfAvailable(false);
                })
                .finally(() => {
                    setIsCheckingPdf(false);
                });
        }
    }, [videoData]);

    const handleClick = () => {
        if (videoData?.mediaBase64) {
            // Open image or PDF in new tab
            window.open(window.location.origin + videoData.mediaBase64, '_blank');
        } else if (videoId) {
            // Show video modal
            onVideoClick(videoId, videoData?.mediaTitle || '');
        }
    };

    if (!videoData) return null;

    const isPdf = videoData.mediaType === 'application/pdf';
    const hasBase64 = Boolean(videoData.mediaBase64);
    const thumbnailUrl = hasBase64 
        ? videoData.mediaBase64 
        : videoId 
        ? `https://img.youtube.com/vi/${videoId}/0.jpg` 
        : null;

    // Truncate title
    const title = videoData.mediaTitle || 'Untitled';
    const truncatedTitle = title.length > 50 ? title.substr(0, 50) + '...' : title;

    return (
        <Card 
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
            onClick={handleClick}
        >
            <div className="relative aspect-video bg-gray-100 overflow-hidden">
                {isPdf && !isPdfAvailable ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        {isCheckingPdf ? (
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        ) : (
                            <p className="text-sm text-muted-foreground px-4 text-center">
                                PDF file not found
                            </p>
                        )}
                    </div>
                ) : isPdf && isPdfAvailable ? (
                    <iframe
                        src={videoData.mediaBase64}
                        className="w-full h-full pointer-events-none"
                        title={title}
                    />
                ) : thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                        <FileText className="h-12 w-12 text-muted-foreground" />
                    </div>
                )}

                {/* Overlay Icon */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                    <div className="bg-card rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        {isPdf ? (
                            <FileText className="h-6 w-6 text-accent" />
                        ) : hasBase64 ? (
                            <ExternalLink className="h-6 w-6 text-accent" />
                        ) : (
                            <Play className="h-6 w-6 text-accent" />
                        )}
                    </div>
                </div>

                {/* Media Type Badge */}
                {isPdf && (
                    <Badge variant="brand" className="absolute top-2 right-2">
                        PDF
                    </Badge>
                )}
            </div>

            <CardContent className="p-4">
                <h3 className="text-sm font-medium text-foreground line-clamp-2 leading-tight">
                    {truncatedTitle}
                </h3>
            </CardContent>
        </Card>
    );
}

export default UserVideoCard;

